import { Subject, Observable } from 'rxjs';
import { filter, mapTo } from 'rxjs/operators';

import {
  Component,
  ViewChild,
  OnDestroy,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  TemplateRef,
  ViewContainerRef,
  EmbeddedViewRef,
  Inject,
} from '@angular/core';
import { trigger } from '@angular/animations';
import { MatSnackBarRef, MAT_SNACK_BAR_DATA } from '@angular/material/snack-bar';
import { SgAsyncGuiContent } from '@sg/ui-kit/async-gui';

import { commonTransitions } from '../animations';

export interface SgToasterComponentOptions {
  duration?: number;
  showClose?: boolean;
  extraClass?: string;
}

export type SgToasterComponentType = 'success' | 'info' | 'warn' | 'error' | 'wait';
export interface SgToasterComponentContext extends SgToasterComponentOptions {
  type: SgToasterComponentType;
  content: SgAsyncGuiContent;
}

interface LocalSgToasterComponentContext extends SgToasterComponentContext {
  progress: number;
}

function iconLookup(type: SgToasterComponentType): string {
  switch (type) {
    case 'success':
      return 'sg_checkmark_circle';
    case 'info':
      return 'sg_bullhorn';
    case 'warn':
      return 'sg_warning';
    case 'error':
      return 'error_outline';
    default:
      return '';
  }
}

/**
 * @internal
 */
export class SgToasterInternalRef {
  get closed(): boolean {
    return this._events.isStopped;
  }

  readonly _events = new Subject<'afterOpened' | 'afterDismissed'>();
  constructor(
    private readonly cmp: SgToasterComponent,
    public readonly item: LocalSgToasterComponentContext
  ) {}

  disableTimeout(): void {
    this.cmp.disableTimeout(this);
  }

  dismissAfterMouseLeave(): void {
    setTimeout(() => this.close(), 2000);
  }

  update(options: SgToasterComponentOptions = {}): void {
    // TODO: re-set based on options, e.g. if duration is set, re-init duration timouts
    this.item.content.icon = iconLookup(this.item.type);
    this.cmp.updateItem(this);
  }

  afterOpened(): Observable<void> {
    return this._events.pipe(
      filter((e) => e === 'afterOpened'),
      mapTo(undefined)
    );
  }

  afterDismissed(): Observable<void> {
    return this._events.pipe(
      filter((e) => e === 'afterDismissed'),
      mapTo(undefined)
    );
  }

  close(): boolean {
    this._events.next('afterDismissed');
    this._events.complete();
    return this.cmp.removeItem(this);
  }
}

export interface SgToasterItemTemplateContext {
  $implicit: SgToasterComponentContext;
  ref: SgToasterInternalRef;
  index: number;
}

interface SgToasterItemContext {
  timer?: number;
  durationStep?: () => void;
  viewRef: EmbeddedViewRef<SgToasterItemTemplateContext>;
}

@Component({
  selector: 'sg-toaster-component',
  templateUrl: './toaster.component.html',
  styleUrls: ['./toaster.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [trigger('detailExpand', commonTransitions.collExpand)],
  standalone: false,
})
export class SgToasterComponent implements OnDestroy {
  @ViewChild('toastersVCRef', { read: ViewContainerRef, static: true }) toastersVCRef: ViewContainerRef;
  @ViewChild('defaultToaster', { read: TemplateRef, static: true })
  defaultToasterTRef: TemplateRef<SgToasterItemTemplateContext>;

  private toasters = new Map<SgToasterInternalRef, SgToasterItemContext>();
  private get toasterRefs(): SgToasterInternalRef[] {
    return Array.from(this.toasters.keys());
  }
  private _inLoop: boolean;

  constructor(
    @Inject(MAT_SNACK_BAR_DATA) public snackBarData: any,
    public cdRef: ChangeDetectorRef,
    public snackBarRef: MatSnackBarRef<SgToasterComponent>
  ) {}

  /**
   * Add's a toaster to the current stack, if no stack will create one.
   * Returns a destroy function.
   */
  addToaster(item: SgToasterComponentContext): SgToasterInternalRef {
    const d: LocalSgToasterComponentContext = Object.create(item);
    d.progress = 0;
    if (!d.content.icon) {
      d.content.icon = iconLookup(d.type);
    }
    const ref = new SgToasterInternalRef(this, d);
    const context: SgToasterItemContext = {} as any;

    const duration = item.hasOwnProperty('duration') ? item.duration : 2000;
    if (duration > 0) {
      let tStart: number;
      context.durationStep = () => {
        if (ref.closed) {
          return; // bail out if the component got closed.
        }
        const passed = performance.now() - tStart;
        d.progress = Math.min(100, (passed / duration) * 100);
        if (d.progress === 100) {
          delete context.durationStep;
        }
      };
      tStart = performance.now();
      context.timer = <any>setTimeout(() => ref.close(), duration);
    }
    const toasterTemplateRef: TemplateRef<SgToasterItemTemplateContext> =
      this.snackBarData.toasterTemplateRef ?? this.defaultToasterTRef;
    context.viewRef = this.toastersVCRef.createEmbeddedView(toasterTemplateRef, {
      $implicit: d,
      ref,
      index: this.toastersVCRef.length,
    });
    this.toasters.set(ref, context);
    this.updateItem(context);

    // This mimics custom component creation when user provides the component
    // currently not implemented, but ready for it.
    setTimeout(() => ref._events.next('afterOpened'));

    return ref;
  }

  ngOnDestroy(): void {
    for (const ref of this.toasterRefs) {
      ref.close();
    }
  }

  disableTimeout(ref: SgToasterInternalRef): void {
    const context = this.toasters.get(ref);
    if (context) {
      if (context.durationStep) {
        delete context.durationStep;
        ref.item.progress = 0;
      }
      if (context.timer) {
        clearInterval(context.timer);
      }
    }
  }

  updateItem(ref: SgToasterInternalRef | SgToasterItemContext): void {
    const context = ref instanceof SgToasterInternalRef ? this.toasters.get(ref) : ref;
    if (context) {
      if (!context.viewRef.destroyed) {
        context.viewRef.markForCheck();
        context.viewRef.detectChanges();
      }
    }
  }

  removeItem(ref: SgToasterInternalRef): boolean {
    const context = this.toasters.get(ref);
    if (context) {
      this.toasters.delete(ref);
      if (context.timer) {
        clearInterval(context.timer);
      }
      context.viewRef.destroy();
      if (this.toasters.size === 0) {
        this.snackBarRef.dismiss();
      }
      return true;
    }
    return false;
  }

  startLoop(): void {
    if (!this._inLoop) {
      this._inLoop = true;
      const run = () => {
        let _inLoop = false;
        for (const ref of this.toasterRefs) {
          const ctx = this.toasters.get(ref);
          if (ctx.durationStep) {
            ctx.durationStep();
            this.updateItem(ctx);
            _inLoop = true;
          }
        }
        if ((this._inLoop = _inLoop)) {
          requestAnimationFrame(run);
        }
      };
      run();
    }
  }
}
